import React, { useMemo } from "react";
import Highcharts from "highcharts";
import { renderToString } from "react-dom/server";
import { Theme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { PerformanceValuationTooltip } from "@components/Tooltip/PerformanceValuationTooltip";
import { ComparisonValue } from "@components";
import { toPercent, useDateFormatterForXAxis } from "@helpers";
import { GainLossChartPercents } from "@components/Tooltip/GainLossChartPercents";
import { InvestmentChartDataItem, usePerformanceTrendColor } from "@hooks";
import { getCommonHighchartsOptions } from "../commonChartOptions";

export const useHighchartsOptions = (
  chartData: {
    totalValues: InvestmentChartDataItem[];
    totalValuesWithDividends: InvestmentChartDataItem[];
    comparisonValues: InvestmentChartDataItem[];
  },
  theme: Theme,
  withDividends: boolean,
  comparedTicker?: ComparisonValue,
  height?: number
): Highcharts.Options => {
  const { t } = useTranslation();
  const commonHighchartsOptions = useMemo(
    () => getCommonHighchartsOptions(theme),
    [theme]
  );

  const activeData = chartData.totalValues;
  const startValue = activeData[0]?.[1] ?? 0;
  const endValue = activeData[activeData.length - 1]?.[1] ?? 0;
  const trendColor = usePerformanceTrendColor(startValue, endValue);
  const xAxisFormatter = useDateFormatterForXAxis();

  return useMemo(() => {
    return {
      credits: {
        enabled: false,
      },
      chart: {
        type: "line",
        ...commonHighchartsOptions.chart,
        ...(height ? { height } : {}),
      },
      title: {
        text: "",
      },
      legend: {
        ...commonHighchartsOptions.legend,
      },
      tooltip: {
        ...commonHighchartsOptions.tooltip,
        split: true,
        formatter: function () {
          return PerformanceValuationTooltip({
            formatter: (value) => (
              <GainLossChartPercents
                theme={theme}
                value={(value as number) / 100}
              />
            ),
            theme: theme,
            points: this.points,
            x: this.x,
          });
        },
      },
      xAxis: {
        type: "datetime",
        tickLength: 0,
        lineColor: theme.palette.border.secondary,
        crosshair: {
          color: theme.palette.border.secondary,
        },
        labels: {
          style: {
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.caption.fontSize,
            color: theme.palette.text.secondary,
            fontWeight: "400",
          },
          formatter: function (
            data: Highcharts.AxisLabelsFormatterContextObject
          ) {
            const selectedDates = this.chart.xAxis[0].getExtremes();
            const startDate = new Date(selectedDates.min);
            const endDate = new Date(selectedDates.max);
            return xAxisFormatter(
              startDate,
              endDate,
              data.value,
              (this.axis as unknown as { tickInterval: number }).tickInterval
            );
          },
        },
      },
      yAxis: {
        title: {
          text: "",
        },
        gridLineColor: theme.palette.border.secondary,
        tickAmount: 6,
        labels: {
          formatter: (data) => {
            return renderToString(
              <div
                style={{
                  fontFamily: theme.typography.fontFamily,
                  fontSize: theme.typography.caption.fontSize,
                  color: theme.palette.text.secondary,
                  fontWeight: 400,
                }}
              >
                {toPercent((data.value as number) / 100, "auto", 0, 0)}
              </div>
            );
          },
        },
      },
      plotOptions: {
        series: {
          marker: {
            enabled: false,
            symbol: "circle",
            states: {
              hover: {
                radius: 4,
                lineWidth: 1,
                lineWidthPlus: 0,
              },
            },
          },
        },
        line: {
          legendSymbol: "rectangle",
          lineWidth: 2,
        },
        area: {
          threshold: null,
          fillColor: {
            stops: [
              [0, Highcharts.color(trendColor).setOpacity(0.1).get("rgba")],
              [1, Highcharts.color(trendColor).setOpacity(0).get("rgba")],
            ],
            linearGradient: {
              x1: 0,
              y1: 0,
              x2: 0,
              y2: 1,
            },
          },
          legendSymbol: "rectangle",
          lineWidth: 2,
        },
      },
      series: [
        {
          id: "total",
          name: t(
            "dashboardPage.dashboard.performance.tabs.performance.portfolio"
          ),
          data: chartData.totalValues,
          color: trendColor,
          type: "area",
        },
        ...(withDividends
          ? [
              {
                id: "totalWithDividends",
                name: t(
                  "dashboardPage.dashboard.performance.tabs.performance.dividends"
                ),
                data: chartData.totalValuesWithDividends,
                color: theme.palette.graphVisual["01"].light,
                type: "line",
              } as Highcharts.SeriesOptionsType,
            ]
          : []),
        ...(comparedTicker
          ? [
              {
                id: "comparison",
                name: comparedTicker.label,
                data: chartData.comparisonValues,
                color: theme.palette.graphVisual["13"].light,
                type: "line",
              } as Highcharts.SeriesOptionsType,
            ]
          : []),
      ],
    } as Highcharts.Options;
  }, [
    chartData,
    height,
    theme,
    withDividends,
    comparedTicker,
    commonHighchartsOptions,
    trendColor,
    t,
    xAxisFormatter,
  ]);
};
